import { useCallback, useEffect, useRef, useState } from 'react';
import { Komunikat, Przycisk } from './ui';

/**
 * Skanowanie kodów kamerą telefonu.
 *
 * Najpierw próbujemy natywnego BarcodeDetector — jest w Chrome i na Androidzie,
 * nic nie waży i działa najszybciej. Gdy go nie ma (Firefox, starsze Safari),
 * dociągamy bibliotekę ZXing dynamicznym importem, żeby nie obciążała pakietu
 * osób, które skanera nie użyją.
 *
 * WAŻNE: przeglądarki nie dają dostępu do kamery poza HTTPS i localhostem.
 * Na produkcji bez certyfikatu ten ekran nie zadziała — dlatego komunikat mówi
 * o tym wprost, zamiast pokazywać pustą ramkę.
 */

type StanSkanera = 'gotowy' | 'uruchamianie' | 'skanuje' | 'blad';

interface Props {
  /** Wywoływane po odczytaniu kodu. Skaner zatrzymuje się do czasu wznowienia. */
  naKod: (kod: string) => void;
  etykieta?: string;
}

const FORMATY = ['qr_code', 'code_128', 'ean_13', 'ean_8', 'code_39', 'data_matrix'];

export function Skaner({ naKod, etykieta = 'Skanuj kod' }: Props) {
  const wideo = useRef<HTMLVideoElement | null>(null);
  const strumien = useRef<MediaStream | null>(null);
  const petla = useRef<number | null>(null);
  const zatrzymajZxing = useRef<(() => void) | null>(null);
  const [stan, setStan] = useState<StanSkanera>('gotowy');
  const [blad, setBlad] = useState<string | null>(null);
  const [sposob, setSposob] = useState<'natywny' | 'zxing' | null>(null);

  const zatrzymaj = useCallback(() => {
    if (petla.current !== null) {
      cancelAnimationFrame(petla.current);
      petla.current = null;
    }
    zatrzymajZxing.current?.();
    zatrzymajZxing.current = null;
    strumien.current?.getTracks().forEach((t) => t.stop());
    strumien.current = null;
    setStan('gotowy');
  }, []);

  useEffect(() => zatrzymaj, [zatrzymaj]);

  async function uruchom() {
    setBlad(null);
    setStan('uruchamianie');

    if (!window.isSecureContext) {
      setStan('blad');
      setBlad(
        'Przeglądarka udostępnia kamerę wyłącznie przez HTTPS. Otwórz system pod adresem ' +
        'z certyfikatem albo użyj czytnika USB, który działa jak klawiatura.',
      );
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      setStan('blad');
      setBlad('Ta przeglądarka nie udostępnia kamery.');
      return;
    }

    try {
      const media = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' } },
      });
      strumien.current = media;
      if (wideo.current) {
        wideo.current.srcObject = media;
        await wideo.current.play();
      }
    } catch (e) {
      setStan('blad');
      const nazwa = (e as { name?: string })?.name;
      setBlad(
        nazwa === 'NotAllowedError'
          ? 'Odmówiono dostępu do kamery. Zezwól na jej użycie w ustawieniach strony i spróbuj ponownie.'
          : nazwa === 'NotFoundError'
            ? 'Nie znaleziono kamery w tym urządzeniu.'
            : 'Nie udało się uruchomić kamery.',
      );
      return;
    }

    setStan('skanuje');

    const Detector = (window as unknown as { BarcodeDetector?: new (o: { formats: string[] }) => {
      detect: (zrodlo: CanvasImageSource) => Promise<{ rawValue: string }[]>;
    } }).BarcodeDetector;

    if (Detector) {
      setSposob('natywny');
      const detektor = new Detector({ formats: FORMATY });
      const krok = async () => {
        if (!wideo.current || !strumien.current) return;
        try {
          const kody = await detektor.detect(wideo.current);
          const pierwszy = kody[0]?.rawValue?.trim();
          if (pierwszy) {
            zatrzymaj();
            naKod(pierwszy);
            return;
          }
        } catch {
          // Klatka nie do odczytania — próbujemy następnej.
        }
        petla.current = requestAnimationFrame(krok);
      };
      petla.current = requestAnimationFrame(krok);
      return;
    }

    // Zapas dla przeglądarek bez natywnego czytnika.
    setSposob('zxing');
    try {
      const { BrowserMultiFormatReader } = await import('@zxing/browser');
      const czytnik = new BrowserMultiFormatReader();
      const kontrolki = await czytnik.decodeFromVideoElement(wideo.current!, (wynik) => {
        const tekst = wynik?.getText()?.trim();
        if (tekst) {
          zatrzymaj();
          naKod(tekst);
        }
      });
      zatrzymajZxing.current = () => kontrolki.stop();
    } catch {
      setStan('blad');
      setBlad('Nie udało się uruchomić czytnika kodów w tej przeglądarce.');
    }
  }

  return (
    <div className="flex flex-col gap-3">
      {blad && <Komunikat rodzaj="uwaga" tytul="Kamera niedostępna">{blad}</Komunikat>}

      <div className="relative overflow-hidden rounded-lg bg-kamien-900"
        style={{ aspectRatio: '4 / 3', display: stan === 'skanuje' || stan === 'uruchamianie' ? 'block' : 'none' }}>
        <video ref={wideo} playsInline muted className="size-full object-cover" />
        {stan === 'skanuje' && (
          <>
            {/* Ramka celownicza — pomaga trafić kodem w kadr. */}
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
              <div className="size-2/3 rounded-lg border-2 border-white/80 shadow-[0_0_0_9999px_rgba(0,0,0,0.35)]" />
            </div>
            <p className="absolute inset-x-0 bottom-2 text-center text-xs text-white/90">
              Skieruj kamerę na kod{sposob === 'zxing' ? ' · czytnik zapasowy' : ''}
            </p>
          </>
        )}
      </div>

      <div className="flex gap-2">
        {stan === 'skanuje' || stan === 'uruchamianie' ? (
          <Przycisk onClick={zatrzymaj}>Zatrzymaj skanowanie</Przycisk>
        ) : (
          <Przycisk wariant="glowny" onClick={() => void uruchom()}>{etykieta}</Przycisk>
        )}
      </div>
    </div>
  );
}
